import { z } from "zod";

// =====================================
// Shared Types
// =====================================
export type SortDirection = "asc" | "desc" | "";

// Trend / summary / per-job-cost chart schemas live in `./chartTypes.ts` to
// keep this file under the per-file line cap. `JobRunTrendPointSchema`
// carries the `dbu_cost` + `cloud_cost` fields the revamped chart mode
// consumes.
export * from "./chartTypes";

// =====================================
// Jobs List
// =====================================
export const JobSummarySchema = z.object({
  job_id: z.string(),
  job_name: z.string(),
  workspace_id: z.string(),
  workspace_name: z.string().nullish(),
  creator_id: z.string().nullish(),
  creator_user_name: z.string().nullish(),
  total_runs: z.number().default(0),
  successful_runs: z.number().default(0),
  failed_runs: z.number().default(0),
  success_rate: z.number().default(0),
  avg_duration_seconds: z.number().default(0),
  last_run_time: z.string().nullish(),
  total_dbus: z.number().default(0),
  // DBU cost — equals total cost, since DBU is the only cost component.
  estimated_cost_usd: z.number().default(0),
  // Cloud-provider cost is not ingested yet, so this is `null`.
  total_cloud_cost: z.number().nullable().optional(),
  // (total_cost / range_days) × 365, computed server-side.
  annualized_total_cost: z.number().default(0),
  // Merged from Postgres Auto Tune config; true when any task is enabled.
  auto_tune_enabled: z.boolean().default(false),
  // Realized savings to date once Auto Tune is enabled.
  realized_savings: z.number().default(0),
  // Estimated annualized savings; shown as a projection when Auto Tune is off.
  estimated_annualized_savings: z.number().default(0),
});

export const JobsResponseSchema = z.object({
  jobs: z.array(JobSummarySchema),
  total_count: z.number(),
  page: z.number().default(1),
  page_size: z.number().default(20),
  total_pages: z.number().default(1),
});

export type JobSummary = z.infer<typeof JobSummarySchema>;
export type JobsResponse = z.infer<typeof JobsResponseSchema>;

export interface JobsParams {
  workspace_id?: string;
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
  days?: number;
  limit?: number;
  sortAttribute?: string;
  sortOrder?: "asc" | "desc" | "";
  job_names?: string[];
  creators?: string[];
  workspaces?: string[];
  auto_tune_enabled?: boolean;
  start_date?: string;
  end_date?: string;
}

// =====================================
// Job Filters
// =====================================
const PaginationMetaSchema = z.object({
  total: z.number().default(0),
  page: z.number().default(1),
  size: z.number().default(20),
  total_pages: z.number().default(1),
});

export const PaginatedFilterOptionsSchema = PaginationMetaSchema.extend({
  items: z.array(z.string()).default([]),
});

export type PaginatedFilterOptions = z.infer<typeof PaginatedFilterOptionsSchema>;

// Creators are returned as { id, name } pairs so the dropdown can show the
// readable display name while still sending the principal id back as the
// filter value.
export const JobCreatorOptionSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
});

export const PaginatedJobCreatorsResponseSchema = PaginationMetaSchema.extend({
  items: z.array(JobCreatorOptionSchema).default([]),
});

export type JobCreatorOption = z.infer<typeof JobCreatorOptionSchema>;
export type PaginatedJobCreatorsResponse = z.infer<typeof PaginatedJobCreatorsResponseSchema>;

// =====================================
// Job Detail
// =====================================
export const JobDetailResponseSchema = z.object({
  job_id: z.string(),
  job_name: z.string(),
  workspace_id: z.string(),
  workspace_name: z.string().nullish(),
  creator_id: z.string().nullish(),
  instance_id: z.union([z.string(), z.number()]).nullish(),
});

export type JobDetailResponse = z.infer<typeof JobDetailResponseSchema>;

// =====================================
// Job Runs
// =====================================
// Closed-set result/trigger constants live in `./runFilterTypes.ts` for the
// same reason chart schemas live in `./chartTypes.ts` — keeps this file under
// the per-file line cap.
export * from "./runFilterTypes";

export const JobRunSchema = z.object({
  run_id: z.string(),
  job_id: z.string(),
  // Databricks `run_id` is workspace-scoped; (workspace_id, instance_id, run_id)
  // is the composite identity used by the Run History row key. `.transform(v => v ?? "")`
  // because `.default("")` only fires on undefined, not null.
  workspace_id: z
    .string()
    .nullish()
    .transform(v => v ?? ""),
  instance_id: z.number().int().nullish(),
  start_time: z.string().nullish(),
  end_time: z.string().nullish(),
  duration_seconds: z.number().nullish(),
  result_state: z.string().nullish(),
  trigger_type: z.string().nullish(),
  // Friendly label for trigger_type derived server-side. Falls back to the
  // raw trigger_type when BE encounters an unknown value, so this is always
  // safe to render without an extra mapping.
  trigger_display: z.string().nullish(),
  estimated_cost_usd: z.number().nullish(),
  cost_cents: z.number().nullish(),
});

export type JobRun = z.infer<typeof JobRunSchema>;

export const JobRunsResponseSchema = z.object({
  runs: z.array(JobRunSchema),
  total_count: z.number(),
  page: z.number().default(1),
  page_size: z.number().default(20),
  total_pages: z.number().default(1),
});

export type JobRunsResponse = z.infer<typeof JobRunsResponseSchema>;

// =====================================
// Job Stats
// =====================================
export const JobStatsSchema = z.object({
  total_runs: z.number().default(0),
  successful_runs: z.number().default(0),
  failed_runs: z.number().default(0),
  // BE returns `null` when total_runs === 0 (semantic "no data" vs "0%");
  // the JobKeyInfo card already guards rendering with stats.total_runs > 0
  // so null only flows through Zod parsing, not display.
  failure_rate: z.number().nullable().default(null),
  avg_duration_seconds: z.number().nullish(),
  min_duration_seconds: z.number().nullish(),
  max_duration_seconds: z.number().nullish(),
  estimated_cost_usd: z.number().nullish(),
});

export type JobStats = z.infer<typeof JobStatsSchema>;

export const JobStatsResponseSchema = z.object({
  stats: JobStatsSchema,
});

export type JobStatsResponse = z.infer<typeof JobStatsResponseSchema>;

// Static job task list (every task in the workflow DAG, including
// tasks that have never run). Sourced from the BE
// /jobs/{id}/tasks-inventory endpoint backed by
// ``dbx_job_tasks_inventory``. Used by the Auto Tune surfaces as the
// authoritative task list for the overview "Tasks" denominator and
// the drawer DAG.
export const JobTaskInventoryItemSchema = z.object({
  task_key: z.string(),
  depends_on_keys: z.array(z.string()).default([]),
  current_worker_node_type: z.string().nullish(),
  current_driver_node_type: z.string().nullish(),
  current_min_workers: z.number().nullish(),
  current_max_workers: z.number().nullish(),
  cluster_name: z.string().nullish(),
  is_auto_scaling: z.boolean().nullish(),
  photon_enabled: z.boolean().nullish(),
  estimated_cost_per_hr: z.number().nullish(),
});

export const JobTasksInventoryResponseSchema = z.object({
  job_id: z.string(),
  tasks: z.array(JobTaskInventoryItemSchema),
  total_count: z.number().default(0),
});

export type JobTaskInventoryItem = z.infer<typeof JobTaskInventoryItemSchema>;
export type JobTasksInventoryResponse = z.infer<typeof JobTasksInventoryResponseSchema>;

// Spark UI deep-link for a Databricks job run (or specific task run within
// it). Sourced from GET /jobs/:jobId/runs/:runId/spark-ui. BE always returns
// 200 with a `state` discriminator; non-success states drive FE behaviour
// (disabled / hidden / toast) without needing HTTP-code reading.
export const JobRunSparkUiStateSchema = z.enum([
  "available",
  "no_spark_context",
  "not_found",
  "permission_denied",
  "workspace_mismatch",
  "upstream_error",
]);

export type JobRunSparkUiState = z.infer<typeof JobRunSparkUiStateSchema>;

export const JobRunSparkUiResponseSchema = z.object({
  state: JobRunSparkUiStateSchema,
  spark_ui_url: z.string().nullish(),
  workspace_host: z.string().nullish(),
  cluster_id: z.string().nullish(),
  spark_context_id: z.string().nullish(),
  message: z.string().nullish(),
  // Native Databricks run page for this run, populated whenever the
  // upstream `runs/get` call succeeds — independent of the
  // spark_ui_url/state split above, which only gates the Spark UI fields.
  run_page_url: z.string().nullish(),
  // Native Databricks run page for the task matched by `task_key` (or
  // `task_run_id`). Populated only when a matching task was found in the
  // `runs/get` response.
  task_run_page_url: z.string().nullish(),
});

export type JobRunSparkUiResponse = z.infer<typeof JobRunSparkUiResponseSchema>;

export * from "./autoTuneTypes";
